/**
 * TileContextMenu — v1.4.30 popup menu that renders on long-press,
 * right-click, or ContextMenu key against any tile / row.
 *
 * Positioning: fixed, anchored to the caller-supplied (x, y) point,
 * with edge-clamping so the menu never overflows the viewport. On
 * TV / mobile the caller can pass a large tap target and the menu
 * still lands in view because we clamp to the innerWidth/innerHeight.
 *
 * Rendering: portalled into <body> so the ancestor tile's overflow /
 * transform / stacking context never clips the menu.
 *
 * Closing rules:
 *   • Any outside pointerdown → close.
 *   • Escape / Back button → close.
 *   • Item selection → run the item's handler THEN close.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function TileContextMenu({
  x = 0,
  y = 0,
  open,
  onClose,
  items,
  title,
  testId,
}) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Position the menu at (x, y) but clamp so we never overflow the
  // viewport. Runs after first paint so we know the menu's real size.
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const el = menuRef.current;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;
    let left = x;
    let top = y;
    if (left + rect.width + margin > vw) left = Math.max(margin, vw - rect.width - margin);
    if (top + rect.height + margin > vh) top = Math.max(margin, vh - rect.height - margin);
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    setPos({ left, top });
    // Autofocus the first non-disabled item for TV-remote users.
    const firstBtn = el.querySelector('button[data-menu-item]:not([disabled])');
    if (firstBtn) firstBtn.focus();
  }, [open, x, y]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Backspace" || e.key === "GoBack") {
        e.preventDefault();
        onClose?.();
      }
    };
    document.addEventListener("pointerdown", onDocDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDocDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const visibleItems = (items || []).filter((it) => it && !it.hidden);

  const node = (
    <div
      ref={menuRef}
      role="menu"
      data-testid={testId || "tile-context-menu"}
      className="fixed z-[10000] min-w-[220px] max-w-[calc(100vw-24px)] rounded-xl border border-white/10 bg-[#0d1017]/98 backdrop-blur-md shadow-2xl py-1.5 text-sm text-slate-100 outline-none"
      style={{ left: pos.left, top: pos.top }}
    >
      {title ? (
        <div className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">
          {title}
        </div>
      ) : null}
      {visibleItems.map((it, i) => {
        if (it.divider) {
          return <div key={`div-${i}`} className="my-1 h-px bg-white/5" />;
        }
        const Icon = it.icon;
        const btnClasses =
          "w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors focus:outline-none " +
          (it.disabled
            ? "opacity-40 cursor-not-allowed"
            : it.destructive
              ? "hover:bg-rose-500/15 focus:bg-rose-500/15 text-rose-200"
              : "hover:bg-white/10 focus:bg-white/10");
        return (
          <button
            key={it.key || i}
            data-menu-item
            data-testid={it.testId}
            disabled={it.disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (it.disabled) return;
              try {
                it.onSelect?.();
              } finally {
                onClose?.();
              }
            }}
            className={btnClasses}
          >
            {Icon ? <Icon className="w-4 h-4 shrink-0 opacity-80" /> : null}
            <span className="flex-1 truncate">{it.label}</span>
          </button>
        );
      })}
    </div>
  );

  return createPortal(node, document.body);
}
